/** ArtifactPackageMembership — links artifact to its onboarding package context */

import { useTranslation } from 'react-i18next'
import { Package } from 'lucide-react'
import { cn } from '../../lib/utils'

interface Props {
  packageId: string | null
  packageName: string | null
  /** Lifecycle stage from the linked package, if available */
  lifecycle?: string | null
  /** Source group tag for this batch→package link */
  sourceGroup?: string | null
  isDark: boolean
}

export function ArtifactPackageMembership({ packageId, packageName, lifecycle, sourceGroup, isDark }: Props) {
  const { t } = useTranslation('artifact')

  const rowClass = cn('flex items-start gap-2 py-1.5 text-xs')
  const labelClass = cn('w-32 shrink-0 font-medium', isDark ? 'text-neutral-400' : 'text-neutral-500')
  const valueClass = cn('flex-1', isDark ? 'text-neutral-200' : 'text-neutral-800')

  return (
    <section aria-label={t('packageMembership.title')} className="space-y-3">
      <h3 className={cn('text-xs font-semibold uppercase tracking-wide', isDark ? 'text-neutral-400' : 'text-neutral-500')}>
        {t('packageMembership.title')}
      </h3>

      <div className={cn('rounded-lg border p-3 space-y-0.5', isDark ? 'border-white/[0.06] bg-surface-2' : 'border-neutral-200 bg-neutral-50')}>
        {packageId ? (
          <>
            {/* Package name */}
            <div className={rowClass}>
              <span className={labelClass}>
                <Package className="inline h-3 w-3 mr-1 opacity-70" />
                {t('packageMembership.package')}
              </span>
              <span className={cn(valueClass, 'font-medium')}>{packageName}</span>
            </div>

            {/* Lifecycle */}
            {lifecycle && (
              <div className={rowClass}>
                <span className={labelClass}>{t('packageMembership.lifecycle')}</span>
                <span className={cn(valueClass, 'capitalize text-[11px]')}>{lifecycle}</span>
              </div>
            )}

            {/* Source group */}
            <div className={rowClass}>
              <span className={labelClass}>{t('packageMembership.sourceGroup')}</span>
              <span className={cn(valueClass, 'text-[11px]')}>
                {sourceGroup ?? '—'}
              </span>
            </div>
          </>
        ) : (
          <p className={cn('text-[11px] py-1', isDark ? 'text-neutral-500' : 'text-neutral-400')}>
            {t('packageMembership.unassigned')}
          </p>
        )}
      </div>
    </section>
  )
}
